/**
 * Owner: Masa Bayu
 * File: geometry.ts
 * Date:Time: 2026-10-14 02:44
 * Description: Line ratio + reachesRight · column ε · R2 §2.
 */

/**
 * Ink reaches column right when end ≥ right − ε (pt).
 * Matches native tip ε (NATIVE_COLUMN_INK_EPS_PT = 2).
 */
export const COLUMN_RIGHT_EPSILON_PT = 2;

/**
 * Minimum word count for body-column membership heuristics.
 * ACCEPTED_LIMITATION ("tiga benua") · no silent fix in R3.
 */
export const MIN_BODY_WORDS = 5;

export interface LineGeometryInput {
  readonly bbox: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
  readonly columnLeftPt: number;
  readonly columnWidthPt: number;
}

export interface LineGeometry {
  readonly ratio: number;
  readonly reachesRight: boolean;
}

/**
 * Geometry for one line against a column.
 * ratio = width / columnWidth · 0 when columnWidth ≤ 0 or width ≤ 0.
 * reachesRight = lineRight ≥ columnRight − COLUMN_RIGHT_EPSILON_PT.
 */
export function computeLineGeometry(input: LineGeometryInput): LineGeometry {
  const { bbox, columnLeftPt, columnWidthPt } = input;
  const columnWidth = columnWidthPt > 0 ? columnWidthPt : 0;
  const ratio =
    columnWidth > 0 && bbox.width > 0 ? bbox.width / columnWidth : 0;
  const columnRight = columnLeftPt + columnWidth;
  const lineRight = bbox.x + bbox.width;
  const reachesRight =
    columnWidth > 0 &&
    lineRight >= columnRight - COLUMN_RIGHT_EPSILON_PT;
  return { ratio, reachesRight };
}
